import React, { useEffect } from 'react'
import CustomTable from 'components/CustomTable'
import { FiGlobe, FiUser } from 'react-icons/fi'
import { Col, Modal, ModalBody, Row } from 'reactstrap'
import { CustomButton, SimpleTitleCard } from 'styles/global'
import { COLUMNS_RESUMO_PRODUTOS } from 'views/simulacao/constants/columns'
import { ApresentacaoValoresWrapper } from 'views/simulacao/style'
import { useSelector } from 'react-redux'
import { ApplicationState } from 'store'
import {
  Referencia as ReferenciaType,
  SIMULACAO
} from 'store/modules/simulacao/types'
import { maskMoney, removeMaskMoney } from 'util/masks'
import * as simulacaoActions from 'store/modules/simulacao/actions'
import useRedux from 'hooks/useRedux'
import history from 'util/history'
import { generateGuid } from 'util/generateGuid'
import { schemaReferencias } from 'views/simulacao/constants/schemas'
import * as yup from 'yup'
import Referencia from '../Referencia'
import { toast } from 'react-toastify'

interface ResumoContratacaoProps {
  isOpen: boolean
  onToggle: () => void
}

const ResumoContratacao = ({ isOpen, onToggle }: ResumoContratacaoProps) => {
  const {
    produtosSelecionados,
    somatorioResumo,
    infosClienteResumo,
    referencias
  } = useSelector<ApplicationState, SIMULACAO>((state) => state.simulacao)

  const { dispatch } = useRedux()

  useEffect(() => {
    dispatch(
      simulacaoActions.setSomatorioResumo(
        produtosSelecionados
          .map((produto) => {
            if (
              produto.servico === 5 ||
              produto.servico === 6 ||
              produto.servico === 7 ||
              produto.servico === 8 ||
              produto.servico === 12
            ) {
              if (!produto.flagSaqueAutorizado && !produto.flagSaqueParcelado) {
                return 0
              } else {
                return Number(removeMaskMoney(produto.valorSaque))
              }
            } else if (produto.servico === 13) {
              let valor = 0
              for (const simulacao of produto.simulacoesCrefaz ?? []) {
                valor += simulacao.selecionados.valorSolicitado
              }
              return valor
            } else {
              return produto.valorTotalFinanciado
            }
          })
          .reduce((prev, curr) => prev + curr, 0)
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isOpen])

  function mudaMeusDados() {
    history.push('/meus-dados')
    onToggle()
  }

  useEffect(() => {
    if (isOpen) {
      dispatch(simulacaoActions.getResumoContratacaoSaga())
      dispatch(simulacaoActions.getPerfisResumoSaga())
      dispatch(simulacaoActions.getTiposContaResumoSaga())
      dispatch(simulacaoActions.getBancosResumoSaga())
      dispatch(simulacaoActions.getConveniosResumoSaga())
      dispatch(simulacaoActions.getReferenciasSaga())
      dispatch(simulacaoActions.getGrausReferenciaSaga())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isOpen])

  const validacaoSchema = (func: () => void) => {
    const temProdutoCrefaz = produtosSelecionados.some((p) => p.servico === 13)
    if (!temProdutoCrefaz) {
      func()
      return
    }

    if (referencias.length < 2) {
      toast.warning('É necessário informar pelo menos 2 referências.')
      return
    }
    schemaReferencias
      .validate(referencias, { abortEarly: false })
      .then(() => {
        func()
      })
      .catch((err) => {
        err.inner.forEach((e: yup.ValidationError) => {
          if (e.path?.includes('[')) {
            const e2 = e.path.match(/\[(\d+)\]/)
            const e3 = e.path.split('.')

            if (e2 && e3.length > 1) {
              const index = Number(e2[1])
              const field = e3[1] as keyof ReferenciaType

              dispatch(
                simulacaoActions.setInvalidReferencia(
                  {
                    invalid: true,
                    message: e.message
                  },
                  field,
                  index
                )
              )
            }
          }
        })
        toast.error(
          'Existem campos inválidos nas referências. Verifique os campos destacados.'
        )
      })
  }

  function concluirContratacao() {
    validacaoSchema(() => dispatch(simulacaoActions.finalizacaoContratacao()))
  }

  const getBancoInfo = (matricula: string) => {
    if (matricula) {
      const dadosBancarios = infosClienteResumo.matriculas?.find(
        (item) => item.numeroMatricula === matricula
      )
      return {
        nomeBanco: dadosBancarios?.descricaoBanco ?? '',
        conta: dadosBancarios?.numeroConta ?? '',
        agencia: dadosBancarios?.numeroAgencia ?? ''
      }
    } else {
      const contaCorrente = infosClienteResumo.contaCorrentePrincipal
      return {
        nomeBanco: contaCorrente?.descricaoBanco ?? '',
        conta: contaCorrente?.conta ?? '',
        agencia: contaCorrente?.agencia ?? ''
      }
    }
  }

  const salvarReferencias = () => {
    validacaoSchema(() => dispatch(simulacaoActions.salvarReferenciasSaga()))
  }

  return (
    <Modal size="xl" isOpen={isOpen} toggle={() => onToggle()}>
      <ModalBody>
        <div className="container-fluid d-flex flex-column row-gap-10">
          <Row className="d-flex justify-content-between">
            <Col md={4}>
              <div
                className="btn-outline-purple justify-content-center"
                onClick={() => {
                  mudaMeusDados()
                }}
              >
                Voltar e alterar
              </div>
            </Col>
          </Row>
          <ApresentacaoValoresWrapper>
            <Row>
              <Col className="d-flex flex-column" sm={6}>
                <Col>
                  <FiUser className="icon" />
                </Col>
                <Col className="d-flex flex-column row-gap-10">
                  <label>Resumo da contratação</label>
                  <span>{infosClienteResumo.nome}</span>
                </Col>
                <Col className="d-flex flex-column align-items-start">
                  <span className="valor-contratacao">
                    R$
                    {maskMoney(somatorioResumo.toFixed(2))}
                  </span>
                  <span>Total da contratação</span>
                </Col>
              </Col>
              <Col className="d-flex align-items-center" sm={6}>
                <CustomButton
                  onClick={() => concluirContratacao()}
                  width={'100%'}
                  height={'50px'}
                  letterSpacing={2}
                >
                  CONCLUIR CONTRATAÇÃO
                </CustomButton>
              </Col>
            </Row>
          </ApresentacaoValoresWrapper>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Nome</label>
              <span>{infosClienteResumo.nome}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Gênero</label>
              <span>{infosClienteResumo.sexo}</span>
            </Col>
          </Row>
          <div className="linha-horizontal"></div>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">RG</label>
              <span>{infosClienteResumo.numeroIdentidade}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Orgão emissor</label>
              <span>{infosClienteResumo.emissorIdentidade}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">UF emissor</label>
              <span>{infosClienteResumo.ufIdentidade}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Data de expedição</label>
              <span>{infosClienteResumo.dataEmissao}</span>
            </Col>
          </Row>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">CPF</label>
              <span>{infosClienteResumo.cpf}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Data de nascimento</label>
              <span>{infosClienteResumo.dataNascimento}</span>
            </Col>
          </Row>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Cidade de nascimento</label>
              <span>{infosClienteResumo.cidadeNascimento}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">UF nascimento</label>
              <span>{infosClienteResumo.ufNascimento}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Nacionalidade</label>
              <span>Brasileira</span>
            </Col>
          </Row>
          <div className="linha-horizontal"></div>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Escolaridade</label>
              <span>{infosClienteResumo.grauInstrucao}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Estado civil</label>
              <span>{infosClienteResumo.estadoCivil}</span>
            </Col>
          </Row>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Nome do Cônjuge</label>
              <span>{infosClienteResumo.nomeConjuge}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Nome da mãe</label>
              <span>{infosClienteResumo.nomeMae}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Nome do pai</label>
              <span>{infosClienteResumo.nomePai}</span>
            </Col>
          </Row>
          <div className="linha-horizontal"></div>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">CEP</label>
              <span>{infosClienteResumo.cep}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Número</label>
              <span>{infosClienteResumo.numeroEndereco}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Complemento</label>
              <span>{infosClienteResumo.complemento}</span>
            </Col>
          </Row>
          <Row className="d-flex justify-content-between">
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Bairro</label>
              <span>{infosClienteResumo.bairro}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">Cidade</label>
              <span>{infosClienteResumo.cidade}</span>
            </Col>
            <Col className="d-flex flex-column" md={3}>
              <label className="label-12">UF</label>
              <span>{infosClienteResumo.uf}</span>
            </Col>
          </Row>
          {produtosSelecionados.some((p) => p.servico === 13) && (
            <>
              <div className="linha-horizontal mb-2"></div>
              <SimpleTitleCard>
                <FiGlobe className="icon" /> Referências
              </SimpleTitleCard>
              {referencias?.map((_, i) => {
                return (
                  <Referencia indexReferencia={i} key={`referencia-${i}`} />
                )
              })}
              <CustomButton
                background="var(--verde-riber)"
                onClick={() => salvarReferencias()}
              >
                Salvar referências
              </CustomButton>
            </>
          )}
          <CustomTable
            data={produtosSelecionados.flatMap((produto) => {
              const bancoInfo = getBancoInfo(produto.matricula)
              if (produto.servico === 13) {
                return (
                  produto.simulacoesCrefaz?.map((simulacao) => {
                    return {
                      id: generateGuid(),
                      servico: produto.servico,
                      banco: 'CREFAZ',
                      matricula: '',
                      valorTotalFinanciado:
                        simulacao.selecionados.valorSolicitado,
                      qtdeParcelas: simulacao.selecionados.prazo ?? 0,
                      valorParcela: simulacao.selecionados.valorParcela ?? 0,
                      ...bancoInfo
                    }
                  }) || []
                )
              }
              return [{ ...produto, ...bancoInfo }]
            })}
            columns={COLUMNS_RESUMO_PRODUTOS}
            paginationMode="client"
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ResumoContratacao
